import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import RemoveFromMailboxModal from "../components/Inbox/RemoveFromMailboxModal";

/**
 * Owns the "Remove from Chris Stratton's mailbox?" confirmation so every
 * Mark reviewed path (footer button, bulk action, attach-to-work-item
 * landing) asks the same question the same way (Matthew, 2026-10-08).
 *
 * `confirmRemoveFromMailbox({ names, count })` resolves to one of
 * `"remove"` (reviewed for both), `"keep"` (reviewed for me only) or
 * `"cancel"` (nothing happens). Only one question is open at a time; a
 * second ask while one is showing cancels the first.
 */

export const REMOVE = "remove";
export const KEEP = "keep";
export const CANCEL = "cancel";

const InboxReviewConfirmContext = createContext(null);

export const InboxReviewConfirmProvider = ({ children }) => {
  const [pending, setPending] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((answer) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    if (resolve) resolve(answer);
  }, []);

  const confirmRemoveFromMailbox = useCallback(
    ({ names = [], count = 1 } = {}) =>
      new Promise((resolve) => {
        if (resolverRef.current) resolverRef.current(CANCEL);
        resolverRef.current = resolve;
        setPending({ names, count });
      }),
    []
  );

  const value = useMemo(
    () => ({ confirmRemoveFromMailbox }),
    [confirmRemoveFromMailbox]
  );

  return (
    <InboxReviewConfirmContext.Provider value={value}>
      {children}
      <RemoveFromMailboxModal
        open={Boolean(pending)}
        names={pending?.names || []}
        count={pending?.count || 1}
        onRemove={() => settle(REMOVE)}
        onKeep={() => settle(KEEP)}
        onCancel={() => settle(CANCEL)}
      />
    </InboxReviewConfirmContext.Provider>
  );
};

InboxReviewConfirmProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * `null` outside a provider — callers treat that as "nobody to ask" and
 * fall back to the backend default (reviewed for both), which is what
 * the app did before the confirmation existed.
 */
export const useInboxReviewConfirm = () => useContext(InboxReviewConfirmContext);

export default InboxReviewConfirmContext;
